/* Distinct scenario panels for /kai-experience.
   Each panel demonstrates a SPECIFIC KAI intelligence pattern — not the same
   chat shell repeated. Some animate, some let the user click through actions,
   some show AI-tagged outputs in real time. */

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, PhoneCall, CheckCircle2, Mic, Sparkles, Loader2, Send,
  ChevronRight, Brain, Activity, AlertTriangle, BellRing, Clock,
  Mail, MessageSquare, ThumbsUp, ThumbsDown, Calendar, User,
  Pencil, Tag, RefreshCw, ArrowRight, Bot, Smile, Frown, Meh,
  TrendingUp, TrendingDown, Heart,
} from "lucide-react";

/* ============================================================================
   SCENARIO 1 — AI RECEPTIONIST
   Animated 3-ring → AI picks up → transcript reveals msg-by-msg → fields
   auto-fill as KAI extracts entities. Click Replay to restart.
   ========================================================================= */

const RECEPTION_TRANSCRIPT = [
  { who: "ring",    txt: "Ring 1…" },
  { who: "ring",    txt: "Ring 2…" },
  { who: "ring",    txt: "Ring 3 — KAI picks up." },
  { who: "agent",   txt: "Hi, this is Aria at Apex Wellness. I'm an AI receptionist filling in while the team is busy. How can I help?" },
  { who: "caller",  txt: "Hey, I'd like to book a physio assessment with Sarah next week if she has time.", extract: { name: null, _: "intent: booking · clinician: Sarah · service: physio assessment" } },
  { who: "agent",   txt: "Of course. Can I grab your full name please?" },
  { who: "caller",  txt: "Yeah, it's Daniel Brooks.", extract: { name: "Daniel Brooks" } },
  { who: "agent",   txt: "Thanks Daniel. What's the best email and phone to reach you?" },
  { who: "caller",  txt: "Email is daniel.brooks@gmail.com, phone is 416-555-0188.", extract: { email: "daniel.brooks@gmail.com", phone: "+1 (416) 555-0188" } },
  { who: "agent",   txt: "Sarah has Tuesday 2pm or Thursday 5:30pm — which works?" },
  { who: "caller",  txt: "Tuesday 2pm please.", extract: { apt_date: "Tue · Apr 14", apt_time: "2:00 PM" } },
  { who: "agent",   txt: "Booked. Any notes for Sarah about why you're coming in?" },
  { who: "caller",  txt: "Lower-back pain after a long hike last weekend.", extract: { apt_notes: "Lower-back pain · post-hike" } },
  { who: "agent",   txt: "Got it. Confirmation just sent. Have a great day!" },
];

export const ScenarioReceptionist = ({ accent }) => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [fields, setFields] = useState({ name: "—", email: "—", phone: "—", apt_date: "—", apt_time: "—", apt_notes: "—" });
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    if (idx >= RECEPTION_TRANSCRIPT.length) { setPlaying(false); return undefined; }
    timer.current = setTimeout(() => {
      const step = RECEPTION_TRANSCRIPT[idx];
      if (step.extract) {
        setFields((f) => ({ ...f, ...step.extract }));
      }
      setIdx((i) => i + 1);
    }, idx === 0 ? 600 : (RECEPTION_TRANSCRIPT[idx]?.who === "ring" ? 900 : 1500));
    return () => clearTimeout(timer.current);
  }, [idx, playing]);

  const replay = () => {
    clearTimeout(timer.current);
    setIdx(0);
    setFields({ name: "—", email: "—", phone: "—", apt_date: "—", apt_time: "—", apt_notes: "—" });
    setPlaying(true);
  };

  const isComplete = idx >= RECEPTION_TRANSCRIPT.length;
  const gradient = `linear-gradient(135deg, ${accent.from}, ${accent.to})`;

  return (
    <div className="grid gap-5 lg:grid-cols-12 lg:gap-6">
      {/* Phone panel */}
      <div className="lg:col-span-7">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border bg-gradient-to-r from-violet-500/[0.08] to-fuchsia-500/[0.08] px-5 py-3.5">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
                <PhoneCall className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-[14px] font-black tracking-tight">Inbound call · Beachview</p>
                <p className="text-[11px] text-muted-foreground">Front desk is on another line — AI taking over after 3 rings.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={replay}
              data-testid="kai-recept-replay"
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-2.5 py-1 text-[11px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
            >
              <RefreshCw className="h-3 w-3" />
              Replay
            </button>
          </div>

          <div className="h-[420px] space-y-2 overflow-y-auto px-5 py-4" data-testid="kai-recept-transcript">
            {RECEPTION_TRANSCRIPT.slice(0, idx).map((m, i) => {
              if (m.who === "ring") {
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-center"
                  >
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
                      <Phone className="h-3 w-3 animate-pulse" />
                      {m.txt}
                    </span>
                  </motion.div>
                );
              }
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.who === "caller" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={
                      m.who === "agent"
                        ? "max-w-[80%] rounded-2xl rounded-tl-sm border border-border bg-secondary/40 px-3 py-2 text-[13px] leading-snug"
                        : "max-w-[80%] rounded-2xl rounded-tr-sm px-3 py-2 text-[13px] font-medium leading-snug text-white shadow-sm"
                    }
                    style={m.who === "caller" ? { backgroundImage: gradient } : undefined}
                  >
                    <p className="mb-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] opacity-70">
                      {m.who === "agent" ? "KAI (AI receptionist)" : "Caller"}
                    </p>
                    {m.txt}
                  </div>
                </motion.div>
              );
            })}

            {playing && !isComplete && (
              <div className="flex justify-start pl-3 text-[11px] text-muted-foreground">
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> listening…
              </div>
            )}

            {isComplete && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-3 flex items-center gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5">
                <CheckCircle2 className="h-4 w-4 flex-none text-emerald-600 dark:text-emerald-400" />
                <p className="text-[12.5px] font-semibold text-emerald-800 dark:text-emerald-200">
                  Lead captured · Booking proposed · Confirmation SMS sent
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Live extraction panel */}
      <div className="lg:col-span-5">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
          <div className="border-b border-border bg-gradient-to-r from-emerald-500/[0.08] to-teal-500/[0.08] px-5 py-3.5">
            <p className="inline-flex items-center gap-1.5 font-display text-[13px] font-black tracking-tight text-emerald-700 dark:text-emerald-300">
              <Brain className="h-4 w-4" />
              KAI extracting fields in real time
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Watch the booking populate as the caller speaks.</p>
          </div>
          <div className="space-y-2.5 p-5">
            {[
              { k: "Name",          v: fields.name,      Icon: User },
              { k: "Email",         v: fields.email,     Icon: Mail },
              { k: "Phone",         v: fields.phone,     Icon: Phone },
              { k: "Appt date",     v: fields.apt_date,  Icon: Calendar },
              { k: "Appt time",     v: fields.apt_time,  Icon: Clock },
              { k: "Notes",         v: fields.apt_notes, Icon: Pencil },
            ].map((row) => {
              const filled = row.v && row.v !== "—";
              return (
                <motion.div
                  key={row.k}
                  layout
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${filled ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-border bg-secondary/30"}`}
                >
                  <row.Icon className={`h-4 w-4 flex-none ${filled ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} />
                  <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{row.k}</p>
                    <p className={`mt-0.5 font-semibold leading-tight ${filled ? "text-foreground" : "text-muted-foreground"}`}>{row.v}</p>
                  </div>
                  {filled && <CheckCircle2 className="h-4 w-4 flex-none text-emerald-500" />}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   SCENARIO 2 — LEAD INTENT (Leads list + KAI Intelligence popover)
   Recreates the real product UI: a leads table with a KAI Intelligence side-popover
   showing Summary · Suggested Next Action · Last Message · Last Call · cached note.
   ========================================================================= */

const LEADS_ROWS = [
  { name: "Ana Canabrava",   email: "canabravaju@gmail.com",  phone: "+1 403 970 2124", src: "Apt form (new cx)",  created: "Jun 13", team: "Meghan Watson", stage: "Booking Requested", value: "$140", lstatus: "Contacted", status: "Open", note: "Left message asking Ana Canabrava to call us back for availability on Saturday mornings.", action: "SMS follow-up", dot: "warn" },
  { name: "KAMAND TAVAKOLI", email: "ktava103@uottawa.ca",    phone: "+1 343 558 5731", src: "Apt form (new cx)",  created: "Jun 5",  team: "Emily Zhang",   stage: "Booking Requested", value: "$140", lstatus: "Contacted", status: "Open", dot: "warn" },
  { name: "Angelica Aite",   email: "angelica09@rogers.com",  phone: "+1 613 406 9325", src: "AI Call > Apt Req",  created: "May 28", team: "Unassigned",    stage: "Booking Requested", value: "$130", lstatus: "New",        status: "Open", dot: "danger" },
  { name: "Dua Fatima",      email: "dua.fatma.1892@gmail.com", phone: "+1 647 609 3085", src: "Apt form (new cx)", created: "May 22", team: "Meghan Watson", stage: "Booking Requested", value: "$140", lstatus: "Contacted", status: "Open", dot: "danger" },
  { name: "Nicholas Lilly",  email: "lillynick143@gmail.com", phone: "+1 613 618 2406", src: "Apt form (new cx)",  created: "May 19", team: "Meghan Watson", stage: "Booking Requested", value: "$140", lstatus: "Contacted", status: "Open", dot: "danger" },
  { name: "Abdulselam Salmo", email: "salmoaboudy@gmail.com", phone: "+1 613 979 2566", src: "Knee Injury Form",   created: "May 14", team: "Emily Zhang",   stage: "New Leads",        value: "$140", lstatus: "Contacted", status: "Open", dot: "info" },
];

const KAI_NOTE = {
  summary: "Left message asking Ana Canabrava to call us back for availability on Saturday mornings.",
  next: "Patient did not reply to the voicemail left about booking.",
  lastMsg: { kind: "TYPE_CALL", dir: "Out", when: "2d ago", body: "(no body)" },
  lastCall: { status: "COMPLETED", duration: "29s", when: "2d ago" },
  factored: "Kai factored in 3 notes on this patient.",
  cached: "Kai · cached 24h · updated 11h ago",
};

export const ScenarioLeadIntent = ({ accent }) => {
  const [openId, setOpenId] = useState(0);
  const gradient = `linear-gradient(135deg, ${accent.from}, ${accent.to})`;

  return (
    <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
      {/* mock browser chrome */}
      <div className="flex items-center justify-between border-b border-border bg-secondary/30 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-rose-400/70" />
          <span className="h-2 w-2 rounded-full bg-amber-400/70" />
          <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
          <span className="ml-3 text-[11px] font-semibold text-muted-foreground">crm.clinicfirst.app / leads</span>
        </div>
        <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          KAI Intelligence · row preview
        </span>
      </div>

      {/* table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[940px] text-[12.5px]">
          <thead className="border-b border-border bg-secondary/40 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            <tr>
              {["", "Contact", "Source", "Created", "FD Team", "Stage", "Value", "Lead Status", "Status"].map((h) => (
                <th key={h} className="px-3 py-2.5 text-left font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LEADS_ROWS.map((r, i) => {
              const open = openId === i;
              const dotMap = { warn: "bg-amber-400", danger: "bg-rose-500", info: "bg-sky-400" };
              return (
                <tr
                  key={i}
                  onClick={() => setOpenId(open ? -1 : i)}
                  data-testid={`kai-lead-row-${i}`}
                  className={`relative cursor-pointer border-b border-border/60 transition-colors hover:bg-secondary/40 ${open ? "bg-violet-500/[0.04]" : ""}`}
                >
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex h-2 w-2 flex-none rounded-full ${dotMap[r.dot]}`} />
                      <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-[10px] font-bold text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
                        <Sparkles className="h-3 w-3" />
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <p className="font-bold text-violet-700 dark:text-violet-300">{r.name}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{r.email}</p>
                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400">{r.phone}</p>
                  </td>
                  <td className="px-3 py-3 align-top">{r.src}</td>
                  <td className="px-3 py-3 align-top">{r.created}</td>
                  <td className="px-3 py-3 align-top">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.team === "Unassigned" ? "bg-rose-500/15 text-rose-600 dark:text-rose-400" : "bg-violet-500/15 text-violet-600 dark:text-violet-300"}`}>{r.team}</span>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-semibold">{r.stage}</span>
                  </td>
                  <td className="px-3 py-3 align-top font-semibold text-emerald-600 dark:text-emerald-400">{r.value}</td>
                  <td className="px-3 py-3 align-top">
                    <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">{r.lstatus}</span>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[11px] font-semibold text-sky-700 dark:text-sky-300">{r.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* KAI Intelligence popover overlay (anchored over the open row) */}
      <AnimatePresence>
        {openId === 0 && (
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.25 }}
            data-testid="kai-lead-popover"
            className="pointer-events-auto absolute left-3 top-[64px] z-20 w-[320px] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl ring-1 ring-inset ring-foreground/5"
          >
            {/* Purple banner header */}
            <div className="relative px-5 py-4" style={{ backgroundImage: gradient }}>
              <div className="flex items-center justify-between">
                <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/95">
                  <Sparkles className="h-3 w-3" />
                  KAI Intelligence
                </p>
                <span className="rounded-full bg-emerald-400 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] text-emerald-950">NEW</span>
              </div>
              <p className="mt-3 font-display text-[18px] font-black tracking-tight text-white">Ana Canabrava</p>
            </div>

            {/* Summary */}
            <div className="border-b border-border px-5 py-3.5">
              <div className="flex items-center justify-between">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Summary</p>
                <button type="button" className="inline-flex items-center gap-1 text-[10.5px] font-bold text-violet-600 dark:text-violet-300">
                  <RefreshCw className="h-3 w-3" /> Refresh
                </button>
              </div>
              <p className="mt-2 text-[12.5px] leading-snug">{KAI_NOTE.summary}</p>
            </div>

            {/* Next action */}
            <div className="px-5 py-3.5">
              <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">
                <Sparkles className="h-3 w-3" /> Suggested next action
              </p>
              <p className="mt-1.5 text-[12.5px] leading-snug">{KAI_NOTE.next}</p>
              <button type="button" data-testid="kai-lead-sms" className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-2.5 text-[12.5px] font-bold text-white shadow-md ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5">
                <MessageSquare className="h-3.5 w-3.5" />
                SMS follow-up
              </button>
            </div>

            {/* Last message */}
            <div className="border-t border-border px-5 py-3.5">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Last message</p>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="rounded-md border border-border bg-secondary/40 px-2 py-0.5 text-[10px] font-mono font-bold">{KAI_NOTE.lastMsg.kind}</span>
                <span className="rounded-md border border-sky-400/40 bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-bold text-sky-700 dark:text-sky-300">→ {KAI_NOTE.lastMsg.dir}</span>
                <span className="text-[10.5px] text-muted-foreground">{KAI_NOTE.lastMsg.when}</span>
              </div>
              <p className="mt-1.5 text-[12px] italic text-muted-foreground">{KAI_NOTE.lastMsg.body}</p>
            </div>

            {/* Last call */}
            <div className="border-t border-border px-5 py-3.5">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Last call</p>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="rounded-md border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-mono font-bold text-emerald-700 dark:text-emerald-300">{KAI_NOTE.lastCall.status}</span>
                <span className="text-[11px] font-semibold">{KAI_NOTE.lastCall.duration}</span>
                <span className="ml-auto text-[10.5px] text-muted-foreground">{KAI_NOTE.lastCall.when}</span>
              </div>
              <p className="mt-2 inline-flex items-center gap-1 text-[10.5px] text-violet-600 dark:text-violet-300">
                <Sparkles className="h-3 w-3" /> {KAI_NOTE.factored}
              </p>
            </div>

            <div className="border-t border-border bg-secondary/30 px-5 py-2 text-[10px] font-semibold text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                {KAI_NOTE.cached}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help caption below table */}
      <p className="border-t border-border bg-secondary/20 px-5 py-3 text-[11.5px] text-muted-foreground">
        <strong className="font-semibold text-foreground/85">Tap any row</strong> to see KAI&apos;s lead intelligence — summary, suggested next action, last message, last call, and how many notes were factored in.
      </p>
    </div>
  );
};

/* ============================================================================
   SCENARIO 3 — INBOX (AI Summary + draft reply + snippets)
   Click "AI Summary" to read the thread; reveals sentiment, suggested response
   and 3 reusable snippet chips a human can click to drop into the textbox.
   ========================================================================= */

const SENTIMENT_THREAD = [
  { who: "patient", at: "9:14 AM", txt: "Hi, I had an appointment last Tuesday and I'm still in pain.", sentiment: 0.45 },
  { who: "patient", at: "9:32 AM", txt: "Tried calling but couldn't reach anyone today.", sentiment: 0.35 },
  { who: "patient", at: "10:48 AM", txt: "Honestly considering cancelling next week. Please respond.", sentiment: 0.18 },
];

const INBOX_SNIPPETS = [
  { label: "Apologise + own it",      text: "Really sorry about that. I'm pulling your chart up now — give me 2 minutes to get back to you with a plan." },
  { label: "Hold appointment",        text: "Keeping your Friday appointment held while we sort this out — no charge whatever you decide." },
  { label: "Escalate to manager",     text: "I'm looping in our clinic manager — she'll personally call you within 30 minutes." },
  { label: "Offer comp session",      text: "We'd like to offer your next session on us as an apology — happy to keep it Friday or move it." },
];

export const ScenarioInbox = ({ accent }) => {
  const [stage, setStage] = useState("thread"); // thread | summarised
  const [reply, setReply] = useState("");
  const [sent, setSent] = useState(false);
  const overall = 0.21;
  const gradient = `linear-gradient(135deg, ${accent.from}, ${accent.to})`;
  const Tone = overall < 0.3 ? Frown : overall < 0.6 ? Meh : Smile;
  const toneColor = overall < 0.3 ? "text-rose-500" : overall < 0.6 ? "text-amber-500" : "text-emerald-500";

  const summarise = () => {
    setStage("summarised");
    setReply("Hi Eleanor — really sorry you're still hurting. I'm pulling your chart up now and I'll have Marcus (your PT) call you within the hour. Want me to keep Friday's appointment held while we sort this out?");
  };
  const reset = () => { setStage("thread"); setReply(""); setSent(false); };
  const appendSnippet = (s) => setReply((r) => (r ? `${r}\n\n${s}` : s));

  return (
    <div className="grid gap-5 lg:grid-cols-12 lg:gap-6">
      {/* Thread */}
      <div className="lg:col-span-7">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-gradient-to-r from-fuchsia-500/[0.10] to-pink-500/[0.10] px-5 py-3.5">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
                <MessageSquare className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-[14px] font-black tracking-tight">SMS · Eleanor Park</p>
                <p className="text-[11px] text-muted-foreground">14 prior visits · last March 4 · Lakeshore</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-700 dark:text-rose-300">
                <AlertTriangle className="h-3 w-3" /> SLA · 17 min
              </span>
              {stage === "thread" ? (
                <button
                  type="button"
                  onClick={summarise}
                  data-testid="kai-inbox-summary"
                  style={{ backgroundImage: gradient }}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[11.5px] font-bold text-white shadow-md ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5"
                >
                  <Sparkles className="h-3 w-3" /> AI Summary
                </button>
              ) : (
                <button type="button" onClick={reset} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-[11.5px] font-semibold transition-all hover:-translate-y-0.5">
                  <RefreshCw className="h-3 w-3" /> Reset
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2 p-5">
            {SENTIMENT_THREAD.map((m, i) => (
              <div key={i} className="flex flex-col items-start">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Patient · {m.at}</span>
                <div className="mt-1 max-w-[85%] rounded-2xl rounded-tl-sm border border-border bg-secondary/40 px-3.5 py-2.5 text-[13px] leading-snug">
                  {m.txt}
                </div>
                <span className={`mt-1 text-[10px] font-semibold ${m.sentiment < 0.3 ? "text-rose-500" : "text-amber-500"}`}>● sentiment {m.sentiment.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Sentiment meter */}
          <div className="border-t border-border bg-secondary/20 px-5 py-3.5">
            <div className="flex items-center gap-3">
              <Tone className={`h-5 w-5 ${toneColor}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between text-[11.5px] font-semibold">
                  <span className="text-muted-foreground">Thread sentiment</span>
                  <span className={`font-mono ${toneColor}`}>{overall.toFixed(2)} · negative</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-border">
                  <div className="h-full rounded-full bg-gradient-to-r from-rose-500 via-amber-500 to-emerald-500" style={{ width: `${overall * 100}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Summary + reply box */}
      <div className="lg:col-span-5">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
          <div className="flex items-center gap-2.5 border-b border-border bg-gradient-to-r from-violet-500/[0.10] to-fuchsia-500/[0.10] px-5 py-3.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <p className="font-display text-[14px] font-black tracking-tight">KAI · Inbox copilot</p>
              <p className="text-[11px] text-muted-foreground">AI summary · drafted reply · clickable snippets</p>
            </div>
          </div>

          <div className="p-5">
            {stage === "thread" && (
              <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-6 text-center">
                <Sparkles className="mx-auto h-7 w-7 text-violet-500" />
                <p className="mt-3 text-[13px] font-semibold">Click <strong>AI Summary</strong></p>
                <p className="mt-1 text-[11.5px] text-muted-foreground">KAI will read the whole thread, score sentiment and prefill a thoughtful reply you can edit or extend with snippets.</p>
              </div>
            )}

            {stage === "summarised" && (
              <AnimatePresence>
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                  {/* Summary block */}
                  <div className="rounded-2xl border border-violet-500/30 bg-violet-500/[0.06] p-3.5">
                    <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700 dark:text-violet-300">
                      <Brain className="h-3 w-3" /> Summary
                    </p>
                    <p className="mt-1.5 text-[12.5px] leading-snug">
                      Eleanor — long-term patient, 14 prior visits — is escalating after a wait-time incident. She&apos;s threatening to cancel her Friday appointment. Sentiment trending negative. KAI recommends an empathetic reply + escalation to manager.
                    </p>
                  </div>

                  {/* Snippets */}
                  <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Snippets · click to insert</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {INBOX_SNIPPETS.map((s) => (
                      <button
                        key={s.label}
                        type="button"
                        onClick={() => appendSnippet(s.text)}
                        data-testid={`kai-inbox-snippet-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80 transition-all hover:-translate-y-0.5 hover:border-violet-500/40 hover:text-foreground"
                      >
                        + {s.label}
                      </button>
                    ))}
                  </div>

                  {/* Reply textarea */}
                  <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Drafted reply</p>
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={5}
                    data-testid="kai-inbox-reply"
                    className="mt-1.5 block w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-[13px] leading-relaxed outline-none ring-ring transition-all focus:ring-2"
                  />

                  {sent ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 flex items-center gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      <p className="text-[12.5px] font-semibold">Reply sent · Manager pinged · Friday hold confirmed.</p>
                    </motion.div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSent(true)}
                      disabled={!reply.trim()}
                      data-testid="kai-inbox-send"
                      style={{ backgroundImage: gradient }}
                      className="mt-3 inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-xl text-[13px] font-bold text-white shadow-md ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    >
                      Send reply <Send className="h-3.5 w-3.5" />
                    </button>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   SCENARIO 4 — PATIENT INSIGHTS (timeline + AI analysis)
   ========================================================================= */

const PT_TIMELINE = [
  { d: "Mar 11", tag: "Visit",     txt: "PT — Marcus Donnell · 45 min · cervical strain follow-up" },
  { d: "Mar 04", tag: "Visit",     txt: "PT — Marcus Donnell · initial assessment · $48.50 co-pay outstanding" },
  { d: "Mar 03", tag: "Form",      txt: "Submitted: New patient intake · auto-attached to chart" },
  { d: "Feb 25", tag: "Cancel",    txt: "Cancelled same-day · reason: 'feeling better'" },
  { d: "Feb 18", tag: "No-show",   txt: "Missed appointment · auto-reschedule SMS sent · no reply" },
  { d: "Feb 04", tag: "Visit",     txt: "PT — Marcus Donnell · 45 min" },
];

export const ScenarioPatient = ({ accent }) => {
  const [analyzed, setAnalyzed] = useState(false);
  const gradient = `linear-gradient(135deg, ${accent.from}, ${accent.to})`;

  return (
    <div className="grid gap-5 lg:grid-cols-12 lg:gap-6">
      {/* Patient profile + timeline */}
      <div className="lg:col-span-7">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
          <div className="border-b border-border bg-gradient-to-r from-emerald-500/[0.10] to-teal-500/[0.10] px-5 py-3.5">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-md ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
                <User className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-[16px] font-black tracking-tight">Jordan Kim · 38 · F</p>
                <p className="text-[11.5px] text-muted-foreground">Beachview · Insurance 80% covered · 6 visits in 90 days · $48.50 outstanding</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Activity timeline</p>
            <ol className="mt-3 space-y-2.5">
              {PT_TIMELINE.map((t, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.25, delay: i * 0.04 }}
                  className="flex items-start gap-3"
                >
                  <span className="w-12 flex-none text-[11px] font-bold text-muted-foreground">{t.d}</span>
                  <span className={`flex-none rounded-md px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] ${t.tag === "No-show" ? "bg-rose-500/15 text-rose-600 dark:text-rose-400" : t.tag === "Cancel" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : t.tag === "Form" ? "bg-sky-500/15 text-sky-600 dark:text-sky-400" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>
                    {t.tag}
                  </span>
                  <span className="flex-1 text-[12.5px] leading-snug">{t.txt}</span>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* AI insights panel */}
      <div className="lg:col-span-5">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
          <div className="flex items-center gap-2.5 border-b border-border bg-gradient-to-r from-violet-500/[0.10] to-fuchsia-500/[0.10] px-5 py-3.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
              <Brain className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <p className="font-display text-[14px] font-black tracking-tight">KAI · Patient intelligence</p>
              <p className="text-[11px] text-muted-foreground">Risk · retention · next-best-action</p>
            </div>
            {!analyzed && (
              <button type="button" onClick={() => setAnalyzed(true)} data-testid="kai-patient-analyze" className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-[12px] font-bold text-white shadow-md transition-all hover:-translate-y-0.5" style={{ backgroundImage: gradient }}>
                Analyze <Sparkles className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="space-y-3 p-5">
            <InsightTile label="Retention risk" value="HIGH" tint="rose" Icon={TrendingDown} sub={analyzed ? "Cancelled + no-show + outstanding balance — 78% churn signal" : "Tap Analyze to score"} hidden={!analyzed} />
            <InsightTile label="Loyalty stage" value="At risk" tint="amber" Icon={Heart} sub={analyzed ? "Was 'Engaged' 30 days ago — now sliding" : "—"} hidden={!analyzed} />
            <InsightTile label="Next best action" value="Win-back call" tint="indigo" Icon={Phone} sub={analyzed ? "Manager call within 24h · waive $48.50 if patient returns" : "—"} hidden={!analyzed} />
            <InsightTile label="Predicted LTV at risk" value="$1,840" tint="emerald" Icon={TrendingUp} sub={analyzed ? "Based on 90-day visit pattern" : "—"} hidden={!analyzed} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   SCENARIO 5 — CALL IQ (Call Details modal style)
   Recreates the real product UI: call meta header · AI Classification card with
   badges + agent + answered-by + patient type + booking type + AI summary +
   Re-run AI button · transcript with timestamps · recording playback bar.
   ========================================================================= */

const CALLIQ_TRANSCRIPT = [
  { who: "agent",  at: "00:00", txt: "Apex Wellness, this is Meghan — how can I help today?" },
  { who: "caller", at: "00:03", txt: "Hi, my back's been hurting for a while. I'd like to come in for an assessment if that's possible." },
  { who: "agent",  at: "00:11", txt: "Of course. Can I get your full name to check if you're an existing patient?" },
  { who: "caller", at: "00:17", txt: "Yeah, it's Layla Henderson — I've come in before, about a year ago." },
  { who: "agent",  at: "00:24", txt: "Found you, Layla. Welcome back. Sarah has Wednesday 11am or Friday 2pm available for an assessment — which works?" },
  { who: "caller", at: "00:35", txt: "Wednesday at 11 please." },
  { who: "agent",  at: "00:39", txt: "Booked. I'll text you the intake form to fill out before Wednesday. See you then." },
];

export const ScenarioCallIQ = ({ accent }) => {
  const [rerunning, setRerunning] = useState(false);
  const gradient = `linear-gradient(135deg, ${accent.from}, ${accent.to})`;
  const rerun = () => { setRerunning(true); setTimeout(() => setRerunning(false), 1400); };

  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-lg">
      {/* Header — call meta */}
      <div className="border-b border-border bg-secondary/30 px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-2xl text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
              <Mic className="h-5 w-5" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-display text-xl font-black tracking-tight sm:text-2xl">Call Details</h3>
                <span className="rounded-md border border-rose-400/40 bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-bold text-rose-700 dark:text-rose-300">No patient linked</span>
              </div>
              <p className="mt-1 text-[12px] text-muted-foreground">
                <span className="font-mono font-bold">Unknown · +1 (416) 728-4407</span> · Apex Wellness · Inbound · Mar 11, 2026 · 9:14 AM
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-3 w-3" /> Completed · 1m 12s
          </span>
        </div>
      </div>

      {/* AI Classification card */}
      <div className="border-b border-border p-5 sm:p-6">
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="inline-flex items-center gap-1.5 font-display text-[14px] font-black tracking-tight text-amber-700 dark:text-amber-300">
              <Sparkles className="h-4 w-4" />
              AI Classification
            </p>
            <button
              type="button"
              onClick={rerun}
              disabled={rerunning}
              data-testid="kai-calliq-rerun"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 text-[11.5px] font-bold text-amber-700 transition-all hover:-translate-y-0.5 disabled:opacity-60 dark:text-amber-300"
            >
              {rerunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Re-run AI
            </button>
          </div>

          <div className="mt-4 grid gap-2.5 text-[12.5px] sm:grid-cols-2 lg:grid-cols-5">
            {[
              { k: "Classification", v: "booked",         tone: "emerald" },
              { k: "Agent",          v: "Meghan Watson",  tone: "indigo" },
              { k: "Answered By",    v: "human",          tone: "sky" },
              { k: "Patient Type",   v: "existing",       tone: "violet" },
              { k: "Booking Type",   v: "New Assessment", tone: "emerald" },
            ].map((r) => (
              <CallIQBadge key={r.k} k={r.k} v={r.v} tone={r.tone} />
            ))}
          </div>

          <div className="mt-4">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">AI Summary</p>
            <p className="mt-1.5 text-[13px] leading-relaxed text-foreground/90">
              Layla Henderson — an existing patient — called requesting an assessment for ongoing lower-back pain. Meghan Watson answered the call, identified Layla in the system and booked her a New Assessment with Sarah for Wednesday 11:00 AM. Intake form sent. Call closed in under 90 seconds.
            </p>
          </div>
        </div>
      </div>

      {/* Transcript */}
      <div className="border-b border-border p-5 sm:p-6">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Transcript</p>
        <div className="mt-3 space-y-2.5">
          {CALLIQ_TRANSCRIPT.map((m, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-12 flex-none font-mono text-[10.5px] font-bold text-muted-foreground">{m.at}</span>
              <span className={`w-16 flex-none text-[10.5px] font-bold uppercase tracking-[0.14em] ${m.who === "agent" ? "text-violet-600 dark:text-violet-300" : "text-emerald-600 dark:text-emerald-400"}`}>
                {m.who === "agent" ? "Agent" : "Caller"}
              </span>
              <span className="flex-1 text-[13px] leading-snug">{m.txt}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recording bar */}
      <div className="flex items-center gap-3 p-5 sm:p-6">
        <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background shadow-md transition-all hover:-translate-y-0.5">
          ▶
        </button>
        <div className="flex-1">
          <div className="flex items-center justify-between text-[10px] font-semibold text-muted-foreground">
            <span>00:00</span>
            <span>01:12</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border">
            <div className="h-full w-[28%] rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
          </div>
        </div>
        <button type="button" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-[11.5px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30">
          <ChevronRight className="h-3 w-3 rotate-90" />
          Download
        </button>
      </div>
    </div>
  );
};

const CallIQBadge = ({ k, v, tone }) => {
  const map = {
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    indigo:  "border-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
    sky:     "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
    violet:  "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  };
  return (
    <div className="rounded-xl border border-border bg-card p-2.5">
      <p className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{k}</p>
      <p className={`mt-1.5 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11.5px] font-bold ${map[tone] || ""}`}>
        {v}
      </p>
    </div>
  );
};

/* ============================================================================
   SCENARIO 6 — KAI ALERTS FEED
   ========================================================================= */

const ALERTS = [
  {
    tone: "warn",  Icon: BellRing, title: "Pending review · Jordan Kim",
    body: "Intake form submitted Mar 3 — still un-reviewed. Sarah's chart audit due in 2h.",
    action: "Open chart", time: "4 min ago",
  },
  {
    tone: "info",  Icon: Clock, title: "Lead going stale · Marcus Tan",
    body: "Website lead not touched in 5 days. KAI is queuing a reactivation SMS at 4pm.",
    action: "Approve SMS", time: "18 min ago",
  },
  {
    tone: "danger", Icon: AlertTriangle, title: "SLA breach · SMS unanswered · Eleanor Park",
    body: "17 min over SLA. KAI drafted a reply on behalf of the manager — awaiting approval.",
    action: "Review draft", time: "now",
  },
  {
    tone: "info",  Icon: MessageSquare, title: "Instagram DM received · @apex.wellness",
    body: "New follower asking about pricing for couples massage. KAI responded with the price sheet and CTA.",
    action: "View thread", time: "32 min ago",
  },
  {
    tone: "warn", Icon: Mail, title: "Email SLA approaching · 3 unread > 2h",
    body: "info@ inbox has 3 patient enquiries pending. KAI will auto-acknowledge in 8 min if no human reply.",
    action: "Open inbox", time: "1h ago",
  },
];

const ALERT_TONES = {
  warn:   { bg: "bg-amber-500/10",  border: "border-amber-500/30",  iconBg: "bg-amber-500",  text: "text-amber-700 dark:text-amber-300" },
  info:   { bg: "bg-sky-500/10",    border: "border-sky-500/30",    iconBg: "bg-sky-500",    text: "text-sky-700 dark:text-sky-300" },
  danger: { bg: "bg-rose-500/10",   border: "border-rose-500/30",   iconBg: "bg-rose-500",   text: "text-rose-700 dark:text-rose-300" },
};

export const ScenarioAlerts = ({ accent }) => {
  const gradient = `linear-gradient(135deg, ${accent.from}, ${accent.to})`;
  return (
    <div>
      <div className="mb-5 flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
            <BellRing className="h-4 w-4" />
          </span>
          <div>
            <p className="font-display text-[14px] font-black tracking-tight">KAI Alerts · live feed</p>
            <p className="text-[11px] text-muted-foreground">Pending reviews · stale leads · SLA breaches · social DMs · email queue</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {ALERTS.length} active · KAI watching
        </span>
      </div>

      <div className="space-y-2.5">
        {ALERTS.map((a, i) => {
          const t = ALERT_TONES[a.tone];
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              data-testid={`kai-alert-${i}`}
              className={`flex items-start gap-3 rounded-2xl border ${t.border} ${t.bg} p-4`}
            >
              <span className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15 ${t.iconBg}`}>
                <a.Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-display text-[14px] font-black tracking-tight">{a.title}</p>
                  <span className="text-[10.5px] text-muted-foreground">· {a.time}</span>
                </div>
                <p className="mt-1 text-[12.5px] leading-snug text-foreground/85">{a.body}</p>
              </div>
              <button type="button" className={`inline-flex h-9 flex-none items-center gap-1 rounded-lg border border-border bg-card px-3 text-[12px] font-bold transition-all hover:-translate-y-0.5 hover:border-foreground/30 ${t.text}`}>
                {a.action} <ChevronRight className="h-3 w-3" />
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

/* ============================================================================
   Helpers
   ========================================================================= */

const Row = ({ k, v }) => (
  <div className="flex items-start gap-2">
    <dt className="w-24 flex-none text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{k}</dt>
    <dd className="flex-1 text-foreground/90">{v}</dd>
  </div>
);

const ClassifyTile = ({ label, value, sub, Icon, tint }) => {
  const map = {
    emerald: "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-700 dark:text-emerald-300",
    rose:    "border-rose-500/30 bg-rose-500/[0.06] text-rose-700 dark:text-rose-300",
    amber:   "border-amber-500/30 bg-amber-500/[0.06] text-amber-700 dark:text-amber-300",
    indigo:  "border-indigo-500/30 bg-indigo-500/[0.06] text-indigo-700 dark:text-indigo-300",
  };
  return (
    <div className={`rounded-xl border p-3 ${map[tint] || ""}`}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <p className="text-[9.5px] font-bold uppercase tracking-[0.16em]">{label}</p>
      </div>
      <p className="mt-1 font-display text-[15px] font-black tracking-tight text-foreground">{value}</p>
      {sub && <p className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">{sub}</p>}
    </div>
  );
};

const InsightTile = ({ label, value, sub, Icon, tint, hidden }) => {
  const map = {
    emerald: "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-700 dark:text-emerald-300",
    rose:    "border-rose-500/30 bg-rose-500/[0.06] text-rose-700 dark:text-rose-300",
    amber:   "border-amber-500/30 bg-amber-500/[0.06] text-amber-700 dark:text-amber-300",
    indigo:  "border-indigo-500/30 bg-indigo-500/[0.06] text-indigo-700 dark:text-indigo-300",
  };
  return (
    <motion.div animate={{ opacity: hidden ? 0.55 : 1 }} className={`rounded-xl border p-3.5 ${map[tint] || ""}`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <p className="text-[9.5px] font-bold uppercase tracking-[0.16em]">{label}</p>
      </div>
      <p className={`mt-1 font-display text-xl font-black tracking-tight ${hidden ? "blur-sm" : ""}`}>{value}</p>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{sub}</p>
    </motion.div>
  );
};
